// import packages
import React, { useState, useEffect, useContext } from "react";
import {
  Button,
  Checkbox,
  Text,
  List,
  Modal,
  OptionList,
  Popover,
  Select,
  InlineStack,
  BlockStack,
  TextField,
} from "@shopify/polaris";
import { useMutation } from "@apollo/client";

// import components
import Banner from "lib/components/banner/banner";
import AutoComplete from "lib/components/autocomplete/autocomplete";

// import helpers
import { baseHelper, errorHelper } from "lib/helpers";

// import context
import { PrivateContext } from "lib/context";

// import constants
import constant from "lib/constant/constant";

// import gql
import { ADD_RULE } from "app/shipping/apollo/mutations";

// import propType

import { addRuleProp } from "app/shipping/modules/operator/features/rule/propTypes";

const AddRule = (props) => {
  const {
    shippingRule: shippingRuleProp = {},
    onClose,
    destinations,
    show,
    bands,
    products,
    handleBanner,
    refetch,
  } = props;
  const { currentUser, cms } = useContext(PrivateContext);

  const defaultRule = {
    condition: "",
    description: "",
    destinationCodes: [],
    isShippingDisabled: false,
    price: 0,
    priceType: "",
    productIds: [],
    shippingBandId: "",
  };
  const defaultBanner = {
    isOpen: false,
    status: "",
    title: "",
  };
  const [shippingRule, setShippingRule] = useState({ ...defaultRule });
  const [banner, setBanner] = useState({ ...defaultBanner });
  const [popoverActive, setPopoverActive] = useState(false);

  const [addRule, { loading: addRuleLoading }] = useMutation(ADD_RULE);

  useEffect(() => {
    if (Object.keys(shippingRuleProp).length !== 0 && shippingRuleProp !== shippingRule) {
      setShippingRule(shippingRuleProp);
    }
  }, [shippingRule, shippingRuleProp]);

  const checkValidation = () => {
    const { shippingBandId, price, description, condition, destinationCodes, productIds, priceType } =
      shippingRule || {};
    if (!shippingBandId) {
      return cms("message.error.selectShippingBand");
    }
    if (!condition) {
      return cms("message.error.selectShippingCondition");
    }
    if (condition === constant.DESTINATION && !(destinationCodes && destinationCodes.length)) {
      return cms("message.error.selectAtleastOneDestination");
    }
    if (condition === constant.PRODUCT && !(productIds && productIds.length)) {
      return cms("message.error.selectAtleastOneProduct");
    }
    if (!description) {
      return cms("message.error.addDescriptionForRule");
    }
    if (description.length > 255) {
      return cms("message.error.descriptionCharacter");
    }
    if (!priceType) {
      return cms("message.error.selectShippingRuleType");
    }
    if (priceType !== constant.FREE && !price) {
      return cms("message.error.provideCost");
    }
    if (priceType !== constant.FREE && price < 0) {
      return cms("message.error.provideValidPrice");
    }
    return false;
  };

  const closeModal = () => {
    setBanner({ ...defaultBanner });
    onClose();
  };

  const addShippingBand = async () => {
    const requestData = {
      ...shippingRule,
    };
    try {
      const response = await addRule({
        variables: { input: requestData },
      });
      const responseError = baseHelper.getResponseError(response.data, constant.gql.CREATE_SHIPPING_RULE);
      if (responseError) {
        setBanner({ isOpen: true, status: "critical", title: responseError });
        return;
      }
      setShippingRule({ ...defaultRule });
      handleBanner({ isOpen: true, status: "success", title: cms("modal.rule.add.message.success.successfullyAdded") });
      setPopoverActive(false);
      closeModal();
      refetch();
    } catch (exception) {
      setBanner({ isOpen: true, status: "critical", title: errorHelper.parse(exception) });
    }
  };

  const onSubmit = () => {
    const hasError = checkValidation();
    if (hasError) {
      setBanner({ isOpen: true, status: "critical", title: hasError });
      return false;
    }
    addShippingBand();
    return true;
  };

  const toggleBanner = () => setBanner({ ...defaultBanner });

  const handleShippingRule = (key, value) => {
    setShippingRule({ ...shippingRule, [key]: value });
  };

  const togglePopover = () => setPopoverActive(!popoverActive);

  const renderDestinations = () => {
    const { destinationCodes = [] } = shippingRule || {};

    const list = destinations.map((item) => {
      const { title, options = [] } = item;
      const selectedOptions = options.filter((option) => destinationCodes.includes(option.value));
      if (!selectedOptions.length) {
        return null;
      }
      return (
        <List.Item key={`${item.title}${selectedOptions.length}`}>
          {title}
          <List type={constant.BULLET}>
            {selectedOptions.map((option) => (
              <List.Item key={option.value}>{option.label}</List.Item>
            ))}
          </List>
        </List.Item>
      );
    });

    const destinationList = list.filter((item) => item);

    if (!destinationList.length) {
      return null;
    }

    return (
      <InlineStack>
        <Text as="p" variant="bodySm">
          {cms("modal.rule.add.label.selected")}
        </Text>
        <List type={constant.TYPE}>{destinationList}</List>
      </InlineStack>
    );
  };

  const {
    shippingBandId,
    price = 0,
    description,
    condition,
    destinationCodes = [],
    productIds = [],
    priceType,
    isShippingDisabled,
  } = shippingRule || {};
  shippingRule.price = parseFloat(price);
  const { moneyFormat = constant.symbol.DOLLAR } = currentUser || {};

  const isFieldDisabled = false;

  const isDestination = condition === constant.DESTINATION;
  const isProduct = condition === constant.PRODUCT;

  const activator = <Button onClick={togglePopover}>{cms("modal.rule.add.button.selectRegion")}</Button>;
  const priceTypeOptions = [
    {
      label: cms("label.fixed"),
      value: constant.FIXED,
    },
    {
      label: cms("modal.rule.add.label.free"),
      value: constant.FREE,
    },
  ];
  const conditionOptions = [
    {
      label: cms("modal.rule.add.label.destination"),
      value: constant.DESTINATION,
    },
    {
      label: cms("modal.rule.add.label.product"),
      value: constant.PRODUCT,
    },
  ];

  const acceptOnlyValidInput = (value, prevValue) => {
    return (baseHelper.validatePositiveNumericValues(value) && value) || (value !== "" && prevValue) || "";
  };

  return (
    <Modal
      open={show}
      onClose={closeModal}
      title={cms("modal.rule.add.title")}
      primaryAction={{
        content: cms("modal.rule.add.button.primary"),
        loading: addRuleLoading,
        onAction: () => onSubmit(),
      }}
    >
      <Modal.Section>
        {banner.isOpen && (
          <>
            <Banner onDismiss={toggleBanner} isOpen={banner.isOpen} status={banner.status} title={banner.title} />
            <br />
          </>
        )}
        <BlockStack gap="200">
          <Select
            options={bands}
            name={constant.SHIPPING_BAND}
            label={cms("modal.rule.add.label.selectShippingBand")}
            placeholder={cms("modal.rule.add.label.selectShippingBand")}
            value={shippingBandId || ""}
            onChange={(val) => handleShippingRule(constant.SHIPPING_BAND_ID, val)}
          />
          <Select
            options={conditionOptions}
            name={constant.SHIPPING_CONDITION}
            label={cms("modal.rule.add.label.selectShippingRule")}
            placeholder={cms("modal.rule.add.label.selectShippingRule")}
            value={condition || ""}
            onChange={(val) => handleShippingRule(constant.CONDITION, val)}
          />
          {isDestination && (
            <>
              <Popover active={popoverActive} activator={activator} onClose={togglePopover} preferredAlignment="left">
                <OptionList
                  onChange={(val) => handleShippingRule(constant.DESTINATION_CODE, val)}
                  sections={destinations}
                  selected={destinationCodes}
                  allowMultiple
                />
              </Popover>
              <Text as="span">{renderDestinations()}</Text>
            </>
          )}
          {isProduct && (
            <AutoComplete
              id={constant.PRODUCT_RULE_CONDITION}
              label={cms("modal.rule.add.label.products")}
              labelHidden
              placeholder={cms("modal.rule.add.placeholder.startTyping")}
              values={products}
              selected={productIds}
              onChange={(val) => handleShippingRule(constant.PRODUCT_IDS, val)}
              minimumSearchLength={constant.value.MIN_SEARCH_LENGTH}
            />
          )}
          <TextField
            id={constant.INPUT_ID_DESCRIPTION}
            type={constant.TEXT}
            label={`${cms("modal.rule.add.label.description")}*`}
            placeholder={cms("modal.rule.add.placeholder.description")}
            value={description}
            onChange={(val) => handleShippingRule(constant.DESCRIPTION, val)}
          />
          <InlineStack gap="400">
            <Select
              options={priceTypeOptions}
              id={constant.INPUT_ID_TYPE}
              name={constant.SHIPPING_TYPE}
              label={cms("modal.rule.add.label.selectShippingType")}
              placeholder={cms("modal.rule.add.placeholder.selectShipping")}
              value={priceType || ""}
              disabled={isFieldDisabled}
              onChange={(val) => handleShippingRule(constant.PRICE_TYPE, val)}
            />
            {priceType === constant.FIXED && (
              <TextField
                id={constant.INPUT_ID_COST}
                label={cms("modal.rule.add.label.amount")}
                min={constant.value.MIN_PRICE}
                step={constant.value.STEP}
                value={price.toString() || ""}
                disabled={isFieldDisabled}
                onChange={(val) => handleShippingRule(constant.PRICE, acceptOnlyValidInput(val), price)}
                prefix={moneyFormat}
              />
            )}
          </InlineStack>
          <Checkbox
            checked={isShippingDisabled}
            onChange={() => handleShippingRule(constant.IS_SHIPPING_DISABLED, !isShippingDisabled)}
            label={cms("modal.rule.add.label.isAvailableForShipping")}
          />
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
};

AddRule.propTypes = addRuleProp.type;

export default AddRule;
